<template>
    <div class="broadcast-messages">
      <!-- Breadcrumb Navigation -->
      <nav class="breadcrumb" aria-label="breadcrumbs">
        <ul>
          <li><router-link to="/admin">Admin Settings</router-link></li>
          <li class="is-active"><a aria-current="page">Broadcast Messages</a></li>
        </ul>
      </nav>
  
      <h1 class="title">Broadcast Messages</h1>
      
      <!-- Actions Header -->
      <div class="level">
        <div class="level-left">
          <button class="button is-link is small" @click="openModalForNew">
            <span class="icon"><i class="fas fa-plus"></i></span>
            <span>New Message</span>
          </button>
        </div>
        <div class="level-right">
          <button class="button is-info is-small" @click="toggleSortOrder">
            Sort By Created Date ({{ sortOrder === 'desc' ? 'Newest First' : 'Oldest First' }})
          </button>
        </div>
      </div>
  
      <!-- Messages Table -->
      <!-- Messages Table -->
      <table class="table is-fullwidth is-striped is-size-7">
        <thead>
          <tr>
            <th>Title</th>
            <th>Content</th>
            <th @click="toggleSortOrder" style="cursor:pointer;">
              Created Date
            </th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr  class="is-size-7" v-for="message in sortedMessages" :key="message.id">
            <td>{{ message.title }}</td>
            <td>
              {{ message.content.substring(0, 50) }}
              <span v-if="message.content.length > 50">...</span>
            </td>
            <td>{{ formatDate(message.created_at) }}</td>
            <td>
              <span class="tag" :class="{'is-success': message.is_active, 'is-danger': !message.is_active}">
                {{ message.is_active ? 'Active' : 'Inactive' }}
              </span>
            </td>
            <td class="buttons">
              <button class="button is-small is-info" @click="openModalForEdit(message)">
                Edit
              </button>
              <button
                class="button is-small"
                :class="message.is_active ? 'is-warning' : 'is-success'"
                @click="toggleActive(message)"
              >
                {{ message.is_active ? 'Deactivate' : 'Activate' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
  
      <!-- Modal for Create / Edit -->
      <div v-if="showModal" class="modal is-active">
        <div class="modal-background" @click="closeModal"></div>
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">{{ modalMode === 'create' ? 'Create Broadcast Message' : 'Edit Broadcast Message' }}</p>
            <button class="delete" aria-label="close" @click="closeModal"></button>
          </header>
          <section class="modal-card-body">
            <form @submit.prevent="submitForm">
              <div class="field">
                <label class="label">Title</label>
                <div class="control">
                  <input class="input" type="text" placeholder="Enter message title" v-model="form.title" required />
                </div>
              </div>
              <div class="field">
                <label class="label">Content</label>
                <div class="control">
                  <textarea class="textarea" placeholder="Enter message content" v-model="form.content" required></textarea>
                </div>
              </div>
              <div class="field">
                <label class="checkbox">
                  <input type="checkbox" v-model="form.is_active" />
                  Active
                </label>
              </div>
              <div class="field is-grouped">
                <div class="control">
                  <button type="submit" class="button is-link">{{ modalMode === 'create' ? 'Create' : 'Update' }}</button>
                </div>
                <div class="control">
                  <button type="button" class="button is-light" @click="closeModal">Cancel</button>
                </div>
              </div>
            </form>
            <!-- Error Message -->
            <div v-if="error" class="notification is-danger">
              {{ error }}
            </div>
            <!-- Success Message -->
            <div v-if="success" class="notification is-success">
              {{ success }}
            </div>
          </section>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  
  export default {
    name: "BroadcastMessages",
    data() {
      return {
        messages: [],
        sortOrder: 'desc', // 'desc' = newest first, 'asc' = oldest first
        showModal: false,
        modalMode: '', // 'create' or 'edit'
        form: {
          id: null,
          title: "",
          content: "",
          is_active: true,
        },
        error: "",
        success: ""
      };
    },
    computed: {
      sortedMessages() {
        return this.messages.slice().sort((a, b) => {
          const dateA = new Date(a.created_at);
          const dateB = new Date(b.created_at);
          return this.sortOrder === 'desc'
            ? dateB - dateA
            : dateA - dateB;
        });
      }
    },
    methods: {
      fetchMessages() {
        axios.get('/api/v1/notifications/broadcast-messages/')
          .then(response => {
            this.messages = response.data;
          })
          .catch(err => {
            this.error = "Failed to fetch messages.";
            console.error(err);
          });
      },
      toggleSortOrder() {
        this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc';
      },
      formatDate(dateStr) {
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateStr).toLocaleDateString(undefined, options);
      },
      openModalForNew() {
        this.modalMode = 'create';
        this.form = {
          id: null,
          title: "",
          content: "",
          is_active: true,
        };
        this.error = "";
        this.success = "";
        this.showModal = true;
      },
      openModalForEdit(message) {
        this.modalMode = 'edit';
        this.form = { ...message };
        this.error = "";
        this.success = "";
        this.showModal = true;
      },
      closeModal() {
        this.showModal = false;
      },
      submitForm() {
        // Depending on mode, either create a new message or update an existing one
        if (this.modalMode === 'create') {
          axios.post('/api/v1/notifications/broadcast-messages/', this.form)
            .then(() => {
              this.success = "Broadcast message created successfully!";
              this.fetchMessages();
              setTimeout(() => {
                this.closeModal();
              }, 1500);
            })
            .catch(err => {
              this.error = err.response?.data?.error || "An error occurred while creating the message.";
              console.error(err);
            });
        } else if (this.modalMode === 'edit') {
          axios.put(`/api/v1/notifications/broadcast-messages/${this.form.id}/`, this.form)
            .then(() => {
              this.success = "Broadcast message updated successfully!";
              this.fetchMessages();
              setTimeout(() => {
                this.closeModal();
              }, 1500);
            })
            .catch(err => {
              this.error = err.response?.data?.error || "An error occurred while updating the message.";
              console.error(err);
            });
        }
      },
      toggleActive(message) {
        // Toggle the active state locally and then update on the backend.
        const updatedStatus = !message.is_active;
        axios.patch(`/api/v1/notifications/broadcast-messages/${message.id}/`, { is_active: updatedStatus })
          .then(() => {
            message.is_active = updatedStatus;
            this.success = "Status updated successfully!";
            setTimeout(() => {
              this.success = "";
            }, 1500);
          })
          .catch(err => {
            this.error = "Failed to update status.";
            console.error(err);
          });
      }
    },
    mounted() {
      this.fetchMessages();
    }
  };
  </script>
  
  <style scoped>
  .broadcast-messages {
    padding: 20px;
  }
  .table th {
    text-align: left;
  }
  .level {
    margin-bottom: 1.5rem;
  }
  .modal-card-body form {
    margin-bottom: 1rem;
  }
  </style>
  